import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Tooltip,
  Card,
  Typography,
  TableProps,
} from "antd"; // Import Typography
import {
  DownOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import PainLevel1 from "../graphics/painLevels/1.svg";
import PainLevel2 from "../graphics/painLevels/2.svg";
import PainLevel3 from "../graphics/painLevels/3.svg";
import PainLevel4 from "../graphics/painLevels/4.svg";
import PainLevel5 from "../graphics/painLevels/5.svg";
import PainLevel6 from "../graphics/painLevels/6.svg";
import PainLevel7 from "../graphics/painLevels/7.svg";
import PainLevel8 from "../graphics/painLevels/8.svg";
import PainLevel9 from "../graphics/painLevels/9.svg";
import PainLevel10 from "../graphics/painLevels/10.svg";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../redux-toolkit/store/store";
import { getPainTrackingEntries } from "../redux-toolkit/slices/dashboardSlice";
import AppPagination from "./Items/AppPagination";
import { formatDateInMMDDYYYY } from "../helpers/date";
import {
  objectifiedDuration,
  objectifiedEnergyLevels,
  objectifiedMoodLevels,
  painEntryFormConstants,
  reliefEffectivenessObjectified,
} from "../helpers/painEntryFormConstants";

const { Title } = Typography; // Destructure Title from Typography

const DashboardPainEntryDatatable = () => {
  const [moodLevels, setMoodLevels] = useState<ObjectifiedData>(
    objectifiedMoodLevels
  );
  const [energyLevels, setEnergyLevels] = useState<ObjectifiedData>(
    objectifiedEnergyLevels
  );
  const [durations, setDurations] =
    useState<ObjectifiedData>(objectifiedDuration);
  const [reliefs, setReliefs] = useState<ObjectifiedData>(
    reliefEffectivenessObjectified
  );
  const painLevelSvgMap: { [key: number]: string } = {
    1: PainLevel1,
    2: PainLevel2,
    3: PainLevel3,
    4: PainLevel4,
    5: PainLevel5,
    6: PainLevel6,
    7: PainLevel7,
    8: PainLevel8,
    9: PainLevel9,
    10: PainLevel10,
  };

  const [activePage, setActivePage] = useState<number>(1);
  const dispatch = useAppDispatch();
  const { isError, trackingEntriesData, isTrackingApiLoading } = useSelector(
    (state: RootState) => state.dashboard
  );
  useEffect(() => {
    dispatch(getPainTrackingEntries(activePage));
  }, []);
  const columns: TableProps<TrackingEntry>["columns"] = [
    {
      title: "Date",
      // dataIndex: "date",
      // key: "date",
      render: (row) => <>{formatDateInMMDDYYYY(row.createdAt, true)}</>,
      sorter: true,
      width: 100,
    },
    {
      title: "Pain rating",
      dataIndex: "painRating",
      key: "painRating",
      sorter: true,
      width: 80,
      render: (rating: any) => (
        <img
          src={painLevelSvgMap[rating]}
          alt="Pain Level"
          style={{ width: "30px", height: "30px" }}
        />
      ),
    },
    {
      title: "Mood",
      // dataIndex: "mood",
      // key: "mood",
      render: (row) => <>{moodLevels[row.moodLevel]}</>,
      sorter: true,
      width: 80,
    },
    {
      title: "Energy Level",
      dataIndex: "energyLevel",
      render: (text) => <>{energyLevels[text]}</>,
      sorter: true,
      width: 80,
    },
    // {
    //   title: "Location",
    //   dataIndex: "location",
    //   key: "location",
    //   sorter: true,
    // },
    {
      title: "Types",
      render: (row) => (
        <>
          {row.painTypes.map((item: string, index: number) => (
            <span key={index}>
              {item}
              {index < row.painTypes.length - 1 && ", "}
            </span>
          ))}
        </>
      ),
    },
    {
      title: "Duration",
      // dataIndex: "duration",
      // key: "duration",
      sorter: true,
      render: (row) => <>{durations[row.painDuration]}</>,
      // width: 100,
    },
    {
      title: "Triggers",
      render: (row) => (
        <>
          {row.triggerTracking.map((item: string, index: number) => (
            <span key={index}>
              {item}
              {index < row.triggerTracking.length - 1 && ", "}
            </span>
          ))}
        </>
      ),
      // dataIndex: "triggers",
      // key: "triggers",
      // sorter: true,
    },
    {
      title: "Relief method",
      render: (row) => (
        <>
          {row.reliefMethods.map((item: string, index: number) => (
            <span key={index}>
              {item}
              {index < row.reliefMethods.length - 1 && ", "}
            </span>
          ))}
        </>
      ),
      // dataIndex: "reliefMethod",
      // key: "reliefMethod",
      // sorter: true,
    },
    {
      title: "Effectiveness",
      // dataIndex: "effectiveness",
      // key: "effectiveness",
      render: (row) => <>{reliefs[row.reliefEffectiveness]}</>,
      sorter: true,
      width: 120,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      sorter: false,
    },
    {
      title: "View",
      key: "view",
      width: 80,
      render: () => (
        <Tooltip title="View More">
          <Button icon={<ArrowRightOutlined />} shape="circle" />
        </Tooltip>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Title level={3}>Pain rating</Title>
        <Space>
          <Button icon={<DownOutlined />} size="large">
            Pain rating
          </Button>
          <Button icon={<CalendarOutlined />} size="large">
            Date
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={trackingEntriesData.entries}
        pagination={false}
        size="middle"
        bordered
        rowClassName="editable-row"
        style={{ overflowX: "auto" }}
      />
      <div className="app-pagination-container">
        <AppPagination
          pagination={{
            currentPage: trackingEntriesData.currentPage,
            nextPage: trackingEntriesData.nextPage,
            totalPages: trackingEntriesData.totalPages,
            totalCount: trackingEntriesData.totalCount,
            previousPage: trackingEntriesData.previousPage,
            pageSize: trackingEntriesData.pageSize,
          }}
          handlePageSwitch={(e) => {
            dispatch(getPainTrackingEntries(e));
          }}
        />
      </div>
    </Card>
  );
};

export default DashboardPainEntryDatatable;
